/**
 * Las fotos propias que se le dan a la IA al crear un sitio (el chip «Fotos»
 * de la caja de la descripción).
 *
 * Se suben en cuanto se eligen, a la biblioteca de la cuenta: todavía no hay
 * proyecto. Al crearlo, `POST /projects` las pasa a ser suyas (`assetIds`).
 *
 * La lista vive en este navegador, como las respuestas de la bienvenida
 * (`welcomeAnswers`): la escribe la pantalla de inicio y la lee el asistente, y
 * sobrevive a cerrar sin crear, igual que el borrador. Quitar una foto de la
 * lista no la borra: queda en la biblioteca, y un borrador ya generado puede
 * estar enseñándola.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { WIZARD_PHOTO_LIMITS } from '@webbuilder/plans'
import { uploadAsset, type BillingPlan } from './api'

/** `order`: cuándo se eligió. Suben de tres en tres y acaban desordenadas, y
    el orden importa: las primeras son las que van a la portada. */
export type WizardPhoto = { id: string; url: string; order: number; width?: number; height?: number }

/** Una foto que todavía sube: su vista local, para enseñarla ya. */
export type PendingPhoto = { key: string; preview: string }

/** Cuántas suben a la vez: más satura la conexión de un teléfono. */
const PARALLEL_UPLOADS = 3

const keyFor = (userId: string) => `veltica:wizard-photos:${userId}`

export function readWizardPhotos(userId: string | null | undefined): WizardPhoto[] {
  if (!userId) return []
  try {
    const raw = globalThis.localStorage.getItem(keyFor(userId))
    const saved = raw ? JSON.parse(raw) as unknown : []
    if (!Array.isArray(saved)) return []
    return saved.flatMap((item): WizardPhoto[] => {
      if (!item || typeof item !== 'object') return []
      const { id, url, order, width, height } = item as Record<string, unknown>
      if (typeof id !== 'string' || typeof url !== 'string') return []
      return [{ id, url, order: typeof order === 'number' ? order : 0, ...(typeof width === 'number' && typeof height === 'number' ? { width, height } : {}) }]
    })
  } catch {
    return []
  }
}

function saveWizardPhotos(userId: string | null | undefined, photos: readonly WizardPhoto[]): void {
  if (!userId) return
  try {
    if (photos.length) globalThis.localStorage.setItem(keyFor(userId), JSON.stringify(photos))
    else globalThis.localStorage.removeItem(keyFor(userId))
  } catch {
    // Sin almacenamiento, las fotos valen mientras esté abierta la pantalla.
  }
}

/** Ya creado el sitio, la próxima vez se empieza sin fotos. */
export function clearWizardPhotos(userId: string | null | undefined): void {
  saveWizardPhotos(userId, [])
}

/** Las fotos que admite su plan; sin saberlo todavía, las del gratis. */
export function wizardPhotoLimit(plan: BillingPlan | null | undefined): number {
  return WIZARD_PHOTO_LIMITS[plan ?? 'free']
}

/** El tamaño de verdad, girado como se ve. Sin él la foto sube igual. */
async function measure(file: File): Promise<{ width: number; height: number } | undefined> {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    const size = { width: bitmap.width, height: bitmap.height }
    bitmap.close?.()
    return size.width > 0 && size.height > 0 ? size : undefined
  } catch {
    return undefined
  }
}

export type WizardPhotos = {
  photos: WizardPhoto[]
  pending: PendingPhoto[]
  limit: number
  /** Lo último que salió mal al añadir: alguna no subió, o no cabían todas. */
  problem: 'failed' | 'limit' | null
  add: (files: readonly File[]) => void
  remove: (id: string) => void
  /** Volver a leer la lista guardada: la pudo cambiar otra pantalla. */
  reload: () => void
}

export function useWizardPhotos(userId: string | undefined, token: string | undefined, limit: number): WizardPhotos {
  const [photos, setPhotos] = useState<WizardPhoto[]>(() => readWizardPhotos(userId))
  const [pending, setPending] = useState<PendingPhoto[]>([])
  const [problem, setProblem] = useState<WizardPhotos['problem']>(null)
  /* Lo que cuenta contra el tope, al momento: dos tandas seguidas no deben
     pasarse por leer un estado que todavía no se ha repintado. */
  const countRef = useRef(0)
  countRef.current = photos.length + pending.length

  const reload = useCallback(() => setPhotos(readWizardPhotos(userId)), [userId])
  useEffect(() => { reload() }, [reload])

  const commit = useCallback((change: (list: WizardPhoto[]) => WizardPhoto[]) => {
    setPhotos((list) => {
      const next = change(list)
      saveWizardPhotos(userId, next)
      return next
    })
  }, [userId])

  const add = useCallback((files: readonly File[]) => {
    if (!token) return
    const images = files.filter((file) => file.type.startsWith('image/'))
    const room = Math.max(0, limit - countRef.current)
    const taken = images.slice(0, room)
    setProblem(images.length > room ? 'limit' : null)
    if (!taken.length) return
    const start = Date.now()
    const queue = taken.map((file, index) => ({ file, order: start + index, key: crypto.randomUUID(), preview: URL.createObjectURL(file) }))
    countRef.current += queue.length
    setPending((list) => [...list, ...queue.map(({ key, preview }) => ({ key, preview }))])

    const upload = async (item: (typeof queue)[number]) => {
      try {
        const size = await measure(item.file)
        /* `describe`: la IA la mira en cuanto llega, mientras se sigue escribiendo. */
        const asset = await uploadAsset(token, item.file, undefined, { size, describe: true })
        if (!asset.id) throw new Error('upload returned no id')
        const id = asset.id
        commit((list) => (list.some((photo) => photo.id === id)
          ? list
          : [...list, { id, url: asset.url, order: item.order, ...size }].sort((a, b) => a.order - b.order)))
      } catch (error) {
        console.error('[wizard-photos] upload failed', error)
        setProblem('failed')
      } finally {
        URL.revokeObjectURL(item.preview)
        setPending((list) => list.filter((entry) => entry.key !== item.key))
      }
    }
    /* De tres en tres; la lista se ordena por `order` al llegar cada una. */
    let next = 0
    const worker = async () => {
      while (next < queue.length) await upload(queue[next++]!)
    }
    void Promise.all(Array.from({ length: Math.min(PARALLEL_UPLOADS, queue.length) }, worker))
  }, [token, limit, commit])

  const remove = useCallback((id: string) => {
    setProblem(null)
    commit((list) => list.filter((photo) => photo.id !== id))
  }, [commit])

  return { photos, pending, limit, problem, add, remove, reload }
}
